import {
  CHART_GROUPS,
  NOT_FOUND,
  ELECTRICAL_GROUPS,
  POWER_QUALITY_MAPPED_NAMES
} from '@/components/shared/visnet-hubs-common-consts';
import { generatePredefinedColor, generateColorForNonMapped } from '../colorGenerationHelpers';
import { generateColorForMappedArray } from '@/utils/colorGenerationHelpers';

const generateName = (feederNumber, phaseNumber, attributeName) => {
  const phaseLabel = `${Number(phaseNumber) === 0 ? 'N' : `L${phaseNumber}`}`;
  let feederLabel = '';

  if (feederNumber !== null) {
    feederLabel = `${Number(feederNumber) === 0 ? 'TX' : `Feeder ${feederNumber}`}`;
  }
  return `${feederLabel} ${phaseLabel} ${attributeName}`.trimLeft();
};

const isShouldBeVisible = ({ feeder, phase, selected }) => {
  const { value, group } = selected;

  switch (true) {
    case value === 0 && group === 'feeder':
      return true;
    case phase === 0:
      return false;
    case phase !== 0 && feeder === 0:
      return false;
    case feeder === 0:
      return false;
    default:
      return true;
  }
};

// Fault Events
const RED = 'rgb(244, 44, 44)';
const GREEN = 'rgb(5, 195, 76)';
const MAPPED_NAMES = {
  Current: {
    label: 'Current Waveform',
    unit: 'A',
    color: RED,
    yAxis: 0
  },
  Voltage: {
    label: 'Voltage Waveform',
    unit: 'V',
    color: GREEN,
    yAxis: 1
  }
};
const GET_PHASES_REGEXP = RegExp(/phase\d/);
const SPLIT_PHASE_REGEXP = RegExp(/(\w*)(\d+)(\w*)/);
const GET_FEEDER_NUMBER_REGEXP = new RegExp(/\d+/);

const createHighchartsSeries = ({ seriesData, group, phaseNumber, feederNumber }) => {
  const { label, unit, yAxis } = MAPPED_NAMES[group];
  return {
    type: 'line',
    boostThreshold: 1,
    turboThreshold: 0,
    animation: false,
    name: generateName(feederNumber, phaseNumber, label),
    data: seriesData,
    group,
    phaseNumber,
    feederNumber,
    suffix: unit,
    yAxis: yAxis
  };
};
const hasFeeders = phaseObject => Object.keys(phaseObject).length;

export const createSeries = (data = []) => {
  const initialMappings = data.reduce((groupedSeriesArray, waveformEvent, index) => {
    groupedSeriesArray.push([]);

    Object.keys(waveformEvent)
      .filter(
        waveformEventKey =>
          GET_PHASES_REGEXP.test(waveformEventKey) && hasFeeders(waveformEvent[waveformEventKey])
      )
      .forEach(phaseKey => {
        Object.keys(waveformEvent[phaseKey]).forEach(feederName => {
          const [feederNumber] = feederName.match(GET_FEEDER_NUMBER_REGEXP);
          const [, , phaseNumber, group] = phaseKey.match(SPLIT_PHASE_REGEXP);

          waveformEvent[phaseKey][feederName].length &&
            groupedSeriesArray[index].push(
              createHighchartsSeries({
                seriesData: waveformEvent[phaseKey][feederName],
                group,
                phaseNumber,
                feederNumber
              })
            );
        });
      });
    return groupedSeriesArray;
  }, []);

  return initialMappings.map(subArray => generateColorForMappedArray(subArray));
};

// Visnethubs
const setElectricalGroup = attribute =>
  ELECTRICAL_GROUPS.find(group => group.names.includes(attribute)) || NOT_FOUND;

const createElectricalChartSerie = ({
  selected,
  dataSeries,
  attributeName,
  phase,
  feeder = null
}) => {
  const { mappedNames, yAxisValue, unit } = setElectricalGroup(attributeName);
  const serieName = generateName(feeder, phase, mappedNames[attributeName]);

  const electricalColorParams = {
    chartGroupType: CHART_GROUPS.ELECTRICAL,
    electricalValues: { phase, feeder },
    attributeName
  };

  return {
    type: 'line',
    boostThreshold: 1,
    turboThreshold: 0,
    animation: false,
    color: generatePredefinedColor(electricalColorParams),
    name: serieName,
    feederNumber: feeder,
    nameValue: mappedNames[attributeName],
    nameInCamelCase: attributeName,
    visible: isShouldBeVisible({ feeder, phase, selected }),
    data: dataSeries,
    yAxis: yAxisValue,
    suffix: unit,
    marker: { enabled: true, radius: 3 },
    states: { hover: { lineWidthPlus: 0 } }
  };
};
const createPowerQualityChartSerie = ({
  selected,
  dataSeries,
  attributeName,
  phase,
  feeder = null
}) => {
  const serieName = generateName(feeder, phase, POWER_QUALITY_MAPPED_NAMES[attributeName]);

  const yAxisValue = attributeName.includes('Thd')
    ? { value: 0, unit: '%' }
    : { value: 1, unit: 'V' };

  return {
    type: 'line',
    boostThreshold: 1,
    turboThreshold: 0,
    animation: false,
    name: serieName,
    nameValue: POWER_QUALITY_MAPPED_NAMES[attributeName],
    nameInCamelCase: attributeName,
    feederNumber: feeder,
    visible: isShouldBeVisible({ feeder, phase, selected }),
    data: dataSeries,
    yAxis: yAxisValue.value,
    suffix: yAxisValue.unit,
    marker: { enabled: true, radius: 3 },
    states: { hover: { lineWidthPlus: 0 } }
  };
};

const dataCreationFunctions = {
  electrical: createElectricalChartSerie,
  powerQuality: createPowerQualityChartSerie
};

const generateChartData = ({ selected, data, chartType, attributesToFilter = null }) => {
  const dataMap = new Map();
  const { phases = [] } = data;

  const serieCreationFunction = dataCreationFunctions[chartType];

  phases.forEach(phase => {
    const { feeders, type: phaseType, id: phaseId, phaseNumber, ...restOfPhaseAttrributes } = phase;

    if (selected.group === 'phase') {
      const phaseKeys = attributesToFilter
        ? Object.keys(restOfPhaseAttrributes).filter(
            attribute => !attributesToFilter.includes(attribute)
          )
        : Object.keys(restOfPhaseAttrributes);

      phaseKeys.forEach(phaseAttribute => {
        if (restOfPhaseAttrributes[phaseAttribute].length) {
          const dataToPass = {
            selected,
            dataSeries: restOfPhaseAttrributes[phaseAttribute],
            attributeName: phaseAttribute,
            phase: phaseNumber
          };

          const phaseDataSerie = serieCreationFunction(dataToPass);

          return dataMap.get(phaseAttribute)
            ? dataMap.get(phaseAttribute).push(phaseDataSerie)
            : dataMap.set(phaseAttribute, [phaseDataSerie]);
        }
        return false;
      });
    }

    feeders.forEach(feeder => {
      const { type: feederType, id: feederId, feederNumber, ...restOfFeederAttrributes } = feeder;

      const objectKeys = attributesToFilter
        ? Object.keys(restOfFeederAttrributes).filter(
            attribute => !attributesToFilter.includes(attribute)
          )
        : Object.keys(restOfFeederAttrributes);

      objectKeys.forEach(feederAttribute => {
        if (restOfFeederAttrributes[feederAttribute].length) {
          const dataToPass = {
            selected,
            dataSeries: restOfFeederAttrributes[feederAttribute],
            attributeName: feederAttribute,
            phase: phaseNumber,
            feeder: feederNumber
          };

          const feederDataSerie = serieCreationFunction(dataToPass);

          return dataMap.get(feederAttribute)
            ? dataMap.get(feederAttribute).push(feederDataSerie)
            : dataMap.set(feederAttribute, [feederDataSerie]);
        }
        return false;
      });
    });
  });

  return generateColorForNonMapped(dataMap);
};

export default generateChartData;
